/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { BUTTON_FORMAT_SOFT, BUTTON_THEME_DEFAULT, Button } from '../Button';
import { SdsIcon } from '../SdsIcon';
import { DataListItem } from './DataList.defs';
import { DataListProps, dataListDefaults } from './DataList.props';

/**
 * SDS DataList component.
 */
export const DataList = (props: DataListProps): JSX.Element => {
  const {
    classes,
    empty,
    headerContent,
    isInteractive,
    isLite,
    limit,
    items,
    showHeader,
    showMoreText,
    title,
    ...rest
  } = {
    ...dataListDefaults,
    ...props,
  };

  const [currentItems, setCurrentItems] = useState<Array<DataListItem>>(items);
  let dataListFooter: JSX.Element | null = null;

  useEffect((): void => {
    const startingItems = limit !== -1 ? [...items].slice(0, limit) : [...items];
    setCurrentItems(startingItems);
  }, [items, limit]);

  // Callbacks
  const showMoreHandler = useCallback((): void => {
    // Add more items to list
    const nextIndexFrom = currentItems.length;
    const nextIndexTo = nextIndexFrom + limit;
    const nextItems = items.slice(nextIndexFrom, nextIndexTo);
    const updatedItems = currentItems.concat(nextItems);

    setCurrentItems(updatedItems);
  }, [currentItems, items, limit, setCurrentItems]);

  // Conditionally render a footer
  if (limit !== -1 && items.length > currentItems.length) {
    dataListFooter = (
      <div className="sds-data-list__footer">
        <Button
          data-id="Data list button: Show more items"
          format={BUTTON_FORMAT_SOFT}
          onClick={showMoreHandler}
          text={showMoreText}
          theme={BUTTON_THEME_DEFAULT}
        />
      </div>
    );
  }

  // Render
  const listProps = { empty, isInteractive } as DataListProps;
  const dataListClass = clsx(`sds-data-list`, classes, {
    [`data-list--lite`]: isLite,
  });

  return (
    <div className={dataListClass} {...rest}>
      {showHeader && (
        <div className="sds-data-list__header">
          <span className="sds-data-list__title">{title}</span>
          {headerContent}
        </div>
      )}
      <div className="sds-data-list__body">{renderList(listProps, currentItems)}</div>
      {dataListFooter}
    </div>
  );
};

/**
 * Click handler for interactive items in the DataList.
 * @param {DataListItem} item The DataListItem that was clicked.
 */
const handleItemClick = (item: DataListItem): void => {
  // Item-specific event
  if (typeof item.action !== 'undefined') {
    item.action();
  }
};

/**
 * List of items in the DataList.
 * @param {DataListProps} props The <DataList /> props.
 * @param {Array<DataListItem>} items The items for the <DataList />.
 * @returns {JSX.Element} A rendered list of items, or an empty footer.
 */
const renderList = (props: DataListProps, items: Array<DataListItem>): JSX.Element => {
  const { isInteractive, empty } = props;

  if (items.length === 0) {
    return <div className="sds-data-list__footer">{empty}</div>;
  }

  const itemElements = items.map((item) => {
    const { content, footer, icon, isCustom } = item;

    const itemContent = isCustom ? (
      <>{content}</>
    ) : (
      <>
        {icon && (
          <div className="sds-data-list__icon">
            <SdsIcon svg={icon} />
          </div>
        )}
        <div className="sds-data-list__content">
          <div>{content}</div>
          <div>{footer}</div>
        </div>
      </>
    );

    return (
      <li className="sds-data-list__row" key={item.key}>
        {isInteractive ? (
          <button
            className="sds-data-list__item"
            data-id={item.dataId}
            onClick={(event): void => {
              event.preventDefault();
              handleItemClick(item);
            }}
          >
            {itemContent}
          </button>
        ) : (
          <div className="sds-data-list__item">{itemContent}</div>
        )}
      </li>
    );
  });

  return <ul className="sds-data-list__list">{itemElements}</ul>;
};
